import { useEffect, useState } from 'react';
import styled, { css, keyframes } from 'styled-components';
import { useGetActivityQuery } from '@shared/api';
import { IconContract, IconInbox } from '@shared/ui/icons';

const pulseDot = keyframes`
  0%, 100% { transform: scale(1);   opacity: 0.85; }
  50%      { transform: scale(1.6); opacity: 0; }
`;

const shimmer = keyframes`
  0%   { background-position: -200% 0; }
  100% { background-position: 200% 0; }
`;

const slideUp = keyframes`
  from { opacity: 0; transform: translateY(8px); }
  to   { opacity: 1; transform: translateY(0); }
`;

const Wrap = styled.section<{ $busy: boolean }>`
  margin-bottom: ${({ theme }) => theme.spacing(5)};
  padding: ${({ theme }) => theme.spacing(4)};
  background: ${({ theme }) => theme.colors.backgroundElevated};
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: ${({ theme }) => theme.radius.lg};
  position: relative;
  overflow: hidden;

  ${({ $busy }) =>
    $busy &&
    css`
      &::before {
        content: '';
        position: absolute;
        top: 0;
        left: 0;
        right: 0;
        height: 2px;
        background: linear-gradient(
          90deg,
          transparent 0%,
          ${({ theme }) => theme.colors.primary} 50%,
          transparent 100%
        );
        background-size: 200% 100%;
        animation: ${shimmer} 1.6s linear infinite;
      }
    `}
`;

const Header = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: ${({ theme }) => theme.spacing(3)};
`;

const HeaderLeft = styled.div`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing(2.5)};
`;

const Title = styled.h2`
  margin: 0;
  font-size: 14px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.08em;
  color: ${({ theme }) => theme.colors.text.secondary};
`;

interface DotProps {
  $variant: 'busy' | 'idle';
}

const StatusDotWrap = styled.div`
  position: relative;
  width: 10px;
  height: 10px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
`;

const StatusDot = styled.div<DotProps>`
  width: 10px;
  height: 10px;
  border-radius: 50%;
  background: ${({ theme }) => theme.colors.status.success};
  position: relative;
  z-index: 1;
  box-shadow: 0 0 8px ${({ theme }) => theme.colors.status.success}88;
`;

const StatusRipple = styled.div<DotProps>`
  position: absolute;
  width: 10px;
  height: 10px;
  border-radius: 50%;
  background: ${({ theme }) => theme.colors.status.success};
  opacity: 0;
  animation: ${pulseDot} ${({ $variant }) => ($variant === 'busy' ? '1.4s' : '2.6s')} ease-out
    infinite;
`;

const Count = styled.div`
  font-size: 12px;
  color: ${({ theme }) => theme.colors.text.muted};
`;

const List = styled.ul`
  list-style: none;
  padding: 0;
  margin: 0;
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing(2)};
`;

const Row = styled.li<{ $delay: number }>`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing(3)};
  padding: ${({ theme }) => theme.spacing(2)} ${({ theme }) => theme.spacing(2.5)};
  border-radius: ${({ theme }) => theme.radius.md};
  background: ${({ theme }) => theme.colors.surface};
  border: 1px solid ${({ theme }) => theme.colors.border};
  font-size: 13px;
  color: ${({ theme }) => theme.colors.text.primary};
  animation: ${slideUp} 360ms ${({ theme }) => theme.motion.easeOut} both;
  animation-delay: ${({ $delay }) => `${$delay}ms`};
  position: relative;
  overflow: hidden;

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 100%;
    background: linear-gradient(
      90deg,
      transparent 0%,
      ${({ theme }) => theme.colors.primary}11 50%,
      transparent 100%
    );
    background-size: 200% 100%;
    animation: ${shimmer} 2.4s linear infinite;
    pointer-events: none;
  }
`;

const RowIcon = styled.div`
  width: 28px;
  height: 28px;
  border-radius: ${({ theme }) => theme.radius.sm};
  background: ${({ theme }) => theme.colors.primarySoft};
  color: ${({ theme }) => theme.colors.primary};
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  z-index: 1;
`;

const RowBody = styled.div`
  flex: 1;
  min-width: 0;
  z-index: 1;
`;

const RowMain = styled.div`
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const RowMeta = styled.div`
  font-size: 11px;
  color: ${({ theme }) => theme.colors.text.muted};
  margin-top: 2px;
`;

const Spinner = styled.div`
  width: 14px;
  height: 14px;
  border: 2px solid ${({ theme }) => theme.colors.border};
  border-top-color: ${({ theme }) => theme.colors.primary};
  border-radius: 50%;
  animation: spin 700ms linear infinite;
  z-index: 1;
  @keyframes spin {
    to {
      transform: rotate(360deg);
    }
  }
`;

function elapsedSince(iso: string): string {
  const ms = Date.now() - new Date(iso).getTime();
  if (ms < 0) return 'только что';
  const sec = Math.round(ms / 1000);
  if (sec < 60) return `${sec} сек`;
  const min = Math.round(sec / 60);
  if (min < 60) return `${min} мин`;
  const hr = Math.round(min / 60);
  return `${hr} ч`;
}

function iconFor(kind: string) {
  switch (kind) {
    case 'contract_analysis':
      return IconContract;
    case 'correspondence_classification':
      return IconInbox;
    default:
      return IconContract;
  }
}

export function ActivityWidget() {
  // Smart polling: 5 сек когда агент реально работает (есть активные
  // операции), 60 сек когда idle. И не дёргает сервер пока вкладка
  // браузера не в фокусе — типичная экономия 80% запросов.
  const [pollMs, setPollMs] = useState(5_000);
  const { data } = useGetActivityQuery(undefined, {
    pollingInterval: pollMs,
    skipPollingIfUnfocused: true,
  });
  const items = data?.items ?? [];
  const busy = items.length > 0;
  useEffect(() => {
    const target = busy ? 5_000 : 60_000;
    setPollMs((prev) => (prev === target ? prev : target));
  }, [busy]);

  if (!busy) return null;

  return (
    <Wrap $busy={busy}>
      <Header>
        <HeaderLeft>
          <StatusDotWrap>
            <StatusRipple $variant="busy" />
            <StatusDot $variant="busy" />
          </StatusDotWrap>
          <Title>Сейчас в работе</Title>
        </HeaderLeft>
        <Count>{`${items.length} ${items.length === 1 ? 'операция' : 'операций'}`}</Count>
      </Header>

      <List>
        {items.slice(0, 6).map((it, idx) => {
          const Icon = iconFor(it.kind);
          return (
            <Row key={`${it.kind}-${it.id}`} $delay={idx * 60}>
              <RowIcon>
                <Icon size={16} />
              </RowIcon>
              <RowBody>
                <RowMain>{it.title}</RowMain>
                <RowMeta>
                  идёт {elapsedSince(it.started_at)}
                  {it.sender ? ` · от ${it.sender}` : ''}
                </RowMeta>
              </RowBody>
              <Spinner />
            </Row>
          );
        })}
      </List>
    </Wrap>
  );
}
